import AppCommandBar from 'app/AppCommandBar'
import { AccountContext } from 'auth'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import { NewsForm, NewsFormSchema, NewsFormValues } from 'forms/NewsForm'
import { CheckType, News, UserRole } from 'gql.generated'
import { omitDeep } from 'helpers'
import {
  useCreateCommand,
  useCustomSearchCommand,
  useDeleteCommand,
  useDeselectCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
// import { useNewNewsContext } from 'hooks/useNewNewsContext'
import { useRouteParams } from 'hooks/useRouteParams'
import { useSelection } from 'hooks/useSelection'
import { pick } from 'lodash'
import { PanelType, SelectionMode } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { ReadOrderIdsQueryVariables } from 'views/AdminOrderIdsView/AdminOrderIdsView.generated'
import { useUpdateWikiCheckMutation } from 'views/AdminWikiView/AdminWikiView.generated'
import { FormType } from 'views/helpers'
import {
  ReadNewsQueryVariables,
  useCreateNewsMutation,
  useDeleteNewsMutation,
  useReadNewsQuery,
  useUpdateNewsMutation,
} from './AdminNewsView.generated'
import { NewsList } from './NewsList'

const AdminNewsListView: React.FunctionComponent = () => {
  const { t } = useTranslation()

  const [createNews] = useCreateNewsMutation()
  const [updateNews] = useUpdateNewsMutation()
  const [deleteNews] = useDeleteNewsMutation()
  const [formType, setFormType] = React.useState<FormType>()
  const [filesUploading, setFilesUploading] = useState(false)
  const [selectedItemId, setSelectedItemId] = React.useState<string>('')
  const [routeParams, updateRouteParams] = useRouteParams('admin.data.news', {
    search: undefined,
  })
  const router = useRouter()
  const paramsNew = router.getState()
  const newElementsCount = paramsNew.params.newsCounter || 0
  const [updateWikiCheck] = useUpdateWikiCheckMutation()

  const currentMicrosoftId: { homeAccount: string } =
    React.useContext(AccountContext)
  // const modifiedNewsCounter = useNewNewsContext()
  const { loading, error, refetch, data } = useReadNewsQuery({
    variables: { search: routeParams.search },
  })
  React.useEffect(() => {
    const updateAsync = async () => {
      await updateWikiCheck({
        variables: {
          update: {
            user: currentMicrosoftId.homeAccount,
            type: CheckType.News,
          },
        },
      })
    }
    updateAsync()
    // modifiedNewsCounter.setRefetchFlag(oldValue => !oldValue)
    // WARNING! Do NOT put modifiedNewsCounter in dependency array.
    // Page will do infinite requests when you do this!
  }, [currentMicrosoftId.homeAccount, updateWikiCheck, data])

  const [newsCreated, setNewsCreated] = useState(false)

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.news.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => updateRouteParams({ search: undefined }),
    },
    [t, routeParams, updateRouteParams]
  )

  const itemsUnsorted: News[] =
    !loading && data && data.multinews
      ? (data.multinews as News[]).filter((x) => !x._deleted)
      : []
  const items = itemsUnsorted.sort((a: News, b: News) => {
    const firstDate = a.created! as Date
    const secondDate = b.created! as Date
    const result = firstDate < secondDate ? -1 : firstDate > secondDate ? 1 : 0
    return result
  })
  const [selection, selectedItems] = useSelection<News>({
    getKey: (item) => item._id!,
  })

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, showDialog, hideDialog] = useDialogState()

  const handleSubmit = async (
    values: NewsFormValues,
    formikActions: FormikActions<NewsFormValues>
  ) => {
    formikActions.setSubmitting(true)
    setFilesUploading(true)
    if (
      values.provingGround.find((prGround) => prGround === '') !== undefined
    ) {
      values.provingGround = []
    }
    if (formType === FormType.insert) {
      await createNews({
        variables: { input: values },
      })
      setNewsCreated(!newsCreated)
    } else if (formType === FormType.update) {
      await updateNews({
        variables: {
          id: selectedItemId,
          update: omitDeep(values as any, ['deletionRule']),
        },
      })
    } else {
      throw new Error(
        ' Custom: Error while OderId creat or update. FormType not set.'
      )
    }

    setFilesUploading(false)
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    await refetch({
      variables: { search: routeParams.search },
    } as ReadOrderIdsQueryVariables)
    hidePanel()
  }

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)
      await deleteNews({
        variables: { ids },
      })
    }
    refetch({
      variables: { search: routeParams.search },
    } as ReadNewsQueryVariables)
    hideDialog()
  }

  /** commands */
  useCreateCommand(
    'news',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({ provingGround: [] })
      },
    },
    [setFormType]
  )
  const handleEdit = () => {
    setFormType(FormType.update)
    setSelectedItemId(selectedItems[0]._id)
    let provingGroundArrayId: string[] = []
    if (selectedItems[0].provingGround.length !== 0) {
      provingGroundArrayId = selectedItems[0].provingGround.map(
        (prGroundElement) => prGroundElement._id
      )
    }
    const existingItem = {
      ...pick(selectedItems[0], [
        'title',
        'provingGround',
        'deletionRule',
        'description',
        'body',
        'files',
        'uploadFiles',
        'uploadPreview',
        'inactive',
      ]),
      provingGround: provingGroundArrayId,
    } as NewsFormValues
    showPanel(existingItem)
  }
  useEditCommand(
    { name: 'news', count: selectedItems.length },
    {
      priority: 20,
      onClick: handleEdit,
    },
    [selectedItems, setFormType]
  )
  useDeleteCommand(
    { name: 'news', count: selectedItems.length },
    {
      priority: 30,
      onClick: showDialog,
    },
    [selectedItems]
  )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <NewsList
        styledElementsCount={newElementsCount}
        items={items}
        selection={selection}
        selectionMode={SelectionMode.single}
        onItemInvoked={handleEdit}
      />
      <FormikPanel
        {...panelState}
        type={PanelType.medium}
        onSubmit={handleSubmit}
        onDismiss={(e) => {
          if (e) {
            hidePanel()
          }
        }}
        validationSchema={new NewsFormSchema()}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('model.news.edit')
            : t('model.news.create')
        }
      >
        <NewsForm disabled={false} filesUploading={filesUploading} />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminNewsView: React.FC = () => (
  <ViewAuthorizer
    roles={[UserRole.SystemAdministrator, UserRole.ResourceManager]}
  >
    <StickyPane>
      <AppCommandBar />
      <AdminNewsListView />
    </StickyPane>
  </ViewAuthorizer>
)
